import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline } from "@/components/primitives";
import { supabase } from "@/integrations/supabase/client";

export default function ResetPassword() {
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  // Supabase puts the recovery token in the URL hash and creates a session
  // when the page loads. We wait for that recovery session before letting
  // the user submit a new password.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setBusy(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      toast.success("Password updated. You're signed in.");
      nav("/", { replace: true });
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't update password");
    } finally {
      setBusy(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 14px",
    border: "1px solid hsl(var(--ink))",
    background: "hsl(var(--paper))",
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 13,
    color: "hsl(var(--ink))",
    outline: "none",
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "60px 20px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="caps">EST. 2026 · ED. 001</span>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>RESET</span>
        </div>

        <div style={{ marginTop: 40 }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>set a new</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 56, letterSpacing: "-0.03em", lineHeight: 0.9, color: "hsl(var(--ink))" }}>
            Password.
          </div>
        </div>

        <div style={{ marginTop: 28 }}><Hairline /></div>

        {!ready ? (
          <p style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))", marginTop: 24, lineHeight: 1.45 }}>
            Open the reset link from your email to continue. If you arrived here by mistake, head back to <a href="/auth" style={{ color: "hsl(var(--rust))", textDecoration: "underline" }}>sign in</a>.
          </p>
        ) : (
          <form onSubmit={submit} style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <label htmlFor="rp-pass" className="caps" style={{ display: "block", marginBottom: 6 }}>New password</label>
              <input id="rp-pass" type="password" required minLength={6} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label htmlFor="rp-conf" className="caps" style={{ display: "block", marginBottom: 6 }}>Confirm</label>
              <input id="rp-conf" type="password" required minLength={6} autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} style={inputStyle} />
            </div>
            <button type="submit" disabled={busy} style={{
              marginTop: 8, padding: "14px 20px",
              background: "hsl(var(--ink))", color: "hsl(var(--paper))",
              border: "1px solid hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
              letterSpacing: "0.18em", textTransform: "uppercase",
              cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
            }}>
              {busy ? "…" : "Update password →"}
            </button>
          </form>
        )}
      </div>
    </MobileFrame>
  );
}
